import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  AchadoAvaliado,
  AssinaturaAvaliada,
  AvaliacaoDoRadarResponse,
  EstadoDaAvaliacao,
  ExploracoesDoRadarResponse,
  buscarAvaliacaoDoRadar,
  buscarExploracoesDoRadar,
} from '../../../api/radarApi';
import ComoFunciona, { ComoFuncionaSecao } from '../../../components/como-funciona/ComoFunciona';
import AtivacaoDosRadaresSection from '../components/AtivacaoDosRadaresSection';
import { useEhAdmin } from '../../../hooks/usePapeis';
import { notify } from '../../../shared/feedback/notify';
import { ADMIN_SISTEMA_CONSUMO_IA } from '../routes';
import '../../../components/como-funciona/help-circle-btn.css';
import '../../../shared/feedback/erro-de-tela.css';
import '../../../shared/ui/badge-status.css';
import '../../../shared/ui/dashboard-base.css'; // casco do modal (`do-modal`, `do-tabela`)
import '../../../shared/ui/modal-close-button.css';
import '../../../components/radar/radar.css'; // `.do-modal-largo`
// Esqueleto de página compartilhado — import explícito, nunca depender de CSS carregado por
// outra tela (lição do BUG-V74, at-67).
import '../../recepcao/pages/Recepcao.css';
import './css/adminSistema.css';

/**
 * Administração do Radar (`/admin/sistema/radar`) — transversal/at-82, ADR 0046.
 *
 * 🔴 **É aqui, e só aqui, que as CONTAGENS de voto aparecem.** Mostrá-las ao lado do botão, na
 * tela de quem vota, enviesaria o voto seguinte (decisão de 06/09/2026) — por isso a separação é
 * de tela, não de permissão.
 *
 * A tela responde três perguntas: o que a equipe aprovou e reprovou (com o que ela escreveu), o
 * que o assistente tentou propor de novo e foi barrado, e quanto cada rodada custou — esta última
 * cruzando com a tela de Consumo de IA.
 *
 * ⚠️ **Zero voto é um estado LEGÍTIMO** desta tela (ninguém votou ainda, e o Radar acabou de
 * subir). Por isso a regra da at-60 vale em dobro: a frase "nenhum voto registrado" só pode
 * aparecer quando a consulta **respondeu**; falha de carga mostra a faixa de erro e mais nada.
 *
 * Sem ação de escrita: a avaliação se faz votando nos próprios radares.
 */

const PERIODOS = [7, 14, 30];

const ROTULOS_DE_MOTIVO: Record<string, string> = {
  UTIL_VOU_VERIFICAR: 'Vou verificar',
  UTIL_JA_AGI: 'Já agi com base nisso',
  JA_SABIA: 'Já sabia',
  NUMERO_NAO_BATE: 'O número não bate',
  IRRELEVANTE: 'Irrelevante',
};

const ROTULOS_DE_ESTADO: Record<EstadoDaAvaliacao, string> = {
  APROVADO: 'Aprovado',
  REPROVADO: 'Reprovado',
  NEUTRO: 'Neutro',
};

const CLASSES_DE_ESTADO: Record<EstadoDaAvaliacao, string> = {
  APROVADO: 'cilia-badge badge-verde',
  REPROVADO: 'cilia-badge badge-vermelho',
  NEUTRO: 'cilia-badge badge-cinza',
};

const CLASSES_DE_STATUS: Record<string, string> = {
  CONCLUIDA: 'cilia-badge badge-verde',
  SEM_PROPOSTA: 'cilia-badge badge-cinza',
  EM_ANDAMENTO: 'cilia-badge badge-azul',
  FALHOU: 'cilia-badge badge-vermelho',
};

const fmtInt = (n: number) => n.toLocaleString('pt-BR');

/** `YYYY-MM-DD` → `DD/MM/YYYY`, sem passar por `Date` (evita deslocamento de fuso). */
function fmtDia(dia: string): string {
  const [ano, mes, d] = dia.split('-');
  return `${d}/${mes}/${ano}`;
}

function fmtInstante(iso: string | null): string {
  if (!iso) return '-';
  return new Date(iso).toLocaleString('pt-BR');
}

/** O recorte por extenso, para a linha da tabela caber numa linha só. */
function descreverParametros(parametros: Record<string, unknown>): string {
  const partes = Object.entries(parametros ?? {}).map(([chave, valor]) => `${chave} = ${valor}`);
  return partes.length > 0 ? partes.join(' · ') : 'sem parâmetros';
}

function secoesDeAjuda(avaliacao: AvaliacaoDoRadarResponse | null): ComoFuncionaSecao[] {
  // 🔴 Os limiares vêm da RESPOSTA, nunca escritos aqui: copiá-los faria esta tela descrever uma
  // régua que o sistema deixou de aplicar no primeiro ajuste — e quem lesse estaria conferindo o
  // texto, não o comportamento.
  const l = avaliacao?.limiares;

  return [
    {
      titulo: 'Ligar e desligar um Radar',
      conteudo: (
        <>
          <p>
            Na aba <strong>Ativação</strong> cada Radar tem o seu próprio interruptor. Desligado, o
            Radar <strong>para por completo</strong>: as análises automáticas não são executadas nos
            horários programados, as varreduras que consultam sistemas externos deixam de rodar,
            nenhuma consulta de inteligência artificial é paga e as telas daquele Radar passam a
            exibir um aviso de que a Administração do Sistema o desativou.
          </p>
          <p>
            Vale <strong>imediatamente</strong>, sem precisar de nova publicação do sistema. E vale
            por Radar: desligar um não afeta o outro.
          </p>
          <p>
            Desligar <strong>não apaga nada</strong>. Todo o histórico de achados e de votos
            continua guardado, e religar não reprocessa o período em que o Radar esteve parado — ele
            volta a analisar a partir do momento em que for ligado.
          </p>
          <p>
            <strong>Desativar não é o mesmo que restringir quem vê.</strong> O interruptor decide se
            o Radar funciona; quem alcança cada Radar é decidido à parte, pelo perfil de cada
            pessoa. Um Radar ligado continua invisível para quem não tem acesso a ele.
          </p>
        </>
      ),
    },
    {
      titulo: 'O que é um Radar e o que esta tela mostra',
      conteudo: (
        <>
          <p>
            Um <strong>Radar</strong> é uma tela que entrega o que merece atenção, em vez de esperar
            alguém procurar. Cada Radar tem duas seções: os <strong>achados pré-definidos</strong>,
            que são consultas fixas do sistema com limite decidido por gente, e os{' '}
            <strong>achados do assistente</strong>, que a inteligência artificial descobre
            explorando consultas pré-aprovadas.
          </p>
          <p>
            Esta tela é sobre a segunda seção: como a equipe está avaliando esses achados, o que já
            foi aprovado ou reprovado, e o histórico das análises. Ela é <strong>somente
            leitura</strong> — a avaliação se faz votando nos próprios Radares.
          </p>
        </>
      ),
    },
    {
      titulo: 'O que o voto faz',
      conteudo: (
        <>
          <p>
            Quem abre um Radar avalia cada achado do assistente com <strong>Útil</strong> ou{' '}
            <strong>Não útil</strong>, e pode acrescentar um motivo e um comentário. O voto não fica
            guardado como opinião: ele muda o que o assistente procura no dia seguinte.
          </p>
          <p>
            Um recorte só muda de estado quando reúne{' '}
            <strong>pelo menos {l ? fmtInt(l.minimoDeVotos) : '...'} votos</strong> e{' '}
            <strong>
              {l ? `${Math.round(l.proporcaoDecisiva * 100)}%` : '...'} deles apontam para o mesmo
              lado
            </strong>
            , contando apenas os votos dos últimos{' '}
            <strong>{l ? fmtInt(l.diasDeAvaliacao) : '...'} dias</strong>. Enquanto isso não
            acontece, ele fica <strong>Neutro</strong> — é por isso que um recorte com quatro votos
            negativos ainda não foi reprovado.
          </p>
        </>
      ),
    },
    {
      titulo: 'Reprovado, aprovado e tema',
      conteudo: (
        <>
          <p>
            <strong>Reprovado</strong>: o assistente deixa de explorar e de propor aquele recorte. A
            proibição é dura — se ele insistir, o próprio sistema barra a proposta, e ela aparece
            aqui marcada como <strong>suprimida</strong>. É o que evita gastar análise repetindo o
            que a equipe já disse não servir.
          </p>
          <p>
            <strong>Aprovado</strong>: o recorte vira <strong>acompanhamento</strong>. Ele passa a
            ser recalculado todos os dias, sem nova busca da inteligência artificial, e aparece no
            Radar num bloco próprio. Continua sendo votado: se deixar de ser útil, ele sai.
          </p>
          <p>
            <strong>Tema</strong> é o padrão entre achados diferentes que compartilham a mesma
            dimensão. Ele só passa a valer como instrução para o assistente a partir de{' '}
            <strong>
              {l ? fmtInt(l.minimoDeAssinaturasPorTema) : '...'} recortes distintos
            </strong>{' '}
            avaliados no mesmo sentido — abaixo disso a coluna mostra o estado, mas ele ainda não
            está agindo.
          </p>
        </>
      ),
    },
    {
      titulo: 'O comentário escrito no voto',
      conteudo: (
        <>
          <p>
            O que a equipe escreve no voto é levado ao assistente na análise seguinte, sempre{' '}
            <strong>declarado como opinião sobre a utilidade do achado</strong> e nunca como ordem.
            É assim que ele generaliza coisas que nenhuma regra fixa expressa, do tipo "estes três
            achados falam todos de sazonalidade".
          </p>
          <p>
            Todo comentário fica gravado com autor e data, e aparece nesta tela. É a única entrada
            de texto de usuário que chega ao modelo em qualquer Radar.
          </p>
        </>
      ),
    },
    {
      titulo: 'O histórico de análises',
      conteudo: (
        <>
          <p>
            Cada Radar analisa os dados uma vez por dia, e o resultado é o mesmo para todos até o
            dia seguinte. A tabela de análises mostra quando cada rodada aconteceu, se foi
            automática (agendada) ou disparada por alguém abrindo a tela, e quanto custou em idas ao
            modelo.
          </p>
          <p>
            <strong>Sem proposta</strong> e <strong>Falhou</strong> são coisas diferentes: no
            primeiro caso as consultas responderam e o assistente concluiu que nada merecia
            atenção — resultado legítimo; no segundo a análise não chegou ao fim, e o Radar diz isso
            na tela em vez de mostrar uma seção vazia.
          </p>
        </>
      ),
    },
  ];
}

const RadarAdminPage: React.FC = () => {
  const [avaliacao, setAvaliacao] = useState<AvaliacaoDoRadarResponse | null>(null);
  const [exploracoes, setExploracoes] = useState<ExploracoesDoRadarResponse | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);

  const [radarSelecionado, setRadarSelecionado] = useState('');
  const [dias, setDias] = useState(30);
  const [filtroDeEstado, setFiltroDeEstado] = useState<'TODOS' | EstadoDaAvaliacao>('TODOS');
  const [assinaturaAberta, setAssinaturaAberta] = useState<AssinaturaAvaliada | null>(null);

  /*
   * at-83: a tela virou DUAS abas com públicos diferentes.
   *
   * 🔴 O TI alcança só **Ativação**. A aba de achados não é apenas escondida — ela não é
   * MONTADA: montá-la faria a tela chamar `/radar/admin/avaliacao`, que responde 403 para ele, e
   * o TI veria uma faixa de erro numa aba que nem deveria existir para o seu perfil.
   */
  const ehAdmin = useEhAdmin();
  const [aba, setAba] = useState<'ativacao' | 'achados'>('ativacao');
  const naAbaDeAchados = ehAdmin && aba === 'achados';

  const carregar = useCallback(async () => {
    if (!ehAdmin || aba !== 'achados') return;
    setCarregando(true);
    try {
      const [dadosDaAvaliacao, dadosDasExploracoes] = await Promise.all([
        buscarAvaliacaoDoRadar(radarSelecionado || undefined, dias),
        buscarExploracoesDoRadar(radarSelecionado || undefined),
      ]);
      setAvaliacao(dadosDaAvaliacao);
      setExploracoes(dadosDasExploracoes);
      setErro(false);
    } catch (error) {
      // Estado seguro E sinal (at-60). "Nenhum voto registrado" é um estado legítimo desta tela,
      // então tabelas vazias vindas de uma falha diriam que a equipe não está avaliando nada — o
      // oposto do que aconteceu.
      setAvaliacao(null);
      setExploracoes(null);
      setErro(true);
      notify.errorFrom(
        error,
        'Não foi possível carregar a avaliação dos Radares. Recarregue a página; se persistir, acione a TI.',
      );
    } finally {
      setCarregando(false);
    }
  }, [radarSelecionado, dias, ehAdmin, aba]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  // `useMemo` na própria lista: sem ele, o `?? []` cria um array novo a cada render e o memo de
  // baixo nunca serviria para nada (é o que o `react-hooks/exhaustive-deps` acusa).
  const radares = useMemo(() => avaliacao?.radares ?? [], [avaliacao]);

  /** As opções do seletor vêm dos próprios dados: não há lista de radares escrita no frontend. */
  const opcoesDeRadar = useMemo(
    () => radares.map((r) => ({ id: r.radarId, rotulo: r.rotulo })),
    [radares],
  );

  const achadosDaAssinatura = (assinatura: string): AchadoAvaliado[] =>
    radares.flatMap((radar) => radar.achados.filter((a) => a.assinatura === assinatura));

  const totalDeVotos = radares.reduce(
    (soma, radar) => soma + radar.assinaturas.reduce((s, a) => s + a.votosUp + a.votosDown, 0),
    0,
  );

  return (
    <div
      className="Frame1000004012"
      style={{
        height: 'fit-content',
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
      }}
    >
      <div className="ConteDo recepcao-conte-do">
        <div className="recepcao-header-row">
          <div className="recepcao-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            Radar
            <button
              type="button"
              className="help-circle-btn"
              title="Como Funciona"
              onClick={() => setIsHelpOpen(true)}
            >
              ?
            </button>
          </div>
        </div>

        <div className="recepcao-tabs" role="tablist" style={{ marginTop: 8 }}>
          <button
            type="button"
            role="tab"
            aria-selected={aba === 'ativacao'}
            className={`recepcao-tab ${aba === 'ativacao' ? 'active' : ''}`}
            onClick={() => setAba('ativacao')}
          >
            Ativação
          </button>
          {ehAdmin && (
            <button
              type="button"
              role="tab"
              aria-selected={aba === 'achados'}
              className={`recepcao-tab ${aba === 'achados' ? 'active' : ''}`}
              onClick={() => setAba('achados')}
            >
              Achados e votos
            </button>
          )}
        </div>

        {aba === 'ativacao' && <AtivacaoDosRadaresSection />}

        {naAbaDeAchados && (
          <>
        <p className="admin-hint">
          Como os achados do assistente estão sendo avaliados pela equipe, em cada Radar, e o
          histórico das análises. <strong>As contagens de voto existem só aqui</strong>: exibi-las
          ao lado do botão, na tela de quem vota, influenciaria o voto seguinte. Esta tela é somente
          leitura.
        </p>

        <div className="admin-form-grid" style={{ marginTop: 12 }}>
          <div className="admin-input-group">
            <label className="admin-label" htmlFor="radar-admin-radar">
              Radar
            </label>
            <select
              id="radar-admin-radar"
              className="admin-select"
              value={radarSelecionado}
              onChange={(evento) => setRadarSelecionado(evento.target.value)}
            >
              <option value="">Todos</option>
              {opcoesDeRadar.map((opcao) => (
                <option key={opcao.id} value={opcao.id}>
                  {opcao.rotulo}
                </option>
              ))}
            </select>
          </div>

          <div className="admin-input-group">
            <label className="admin-label" htmlFor="radar-admin-periodo">
              Período dos achados
            </label>
            <select
              id="radar-admin-periodo"
              className="admin-select"
              value={dias}
              onChange={(evento) => setDias(Number(evento.target.value))}
            >
              {PERIODOS.map((periodo) => (
                <option key={periodo} value={periodo}>
                  Últimos {periodo} dias
                </option>
              ))}
            </select>
          </div>
        </div>

        {erro && (
          <p className="erro-de-tela">
            Não foi possível carregar a avaliação dos Radares. Esta tela não está dizendo que
            ninguém avaliou nada — ela não conseguiu verificar. Recarregue a página; se o problema
            persistir, acione a TI.
          </p>
        )}

        {carregando && !erro && <p className="do-kpi-carregando">Carregando a avaliação...</p>}

        {!carregando && !erro && avaliacao && radares.length === 0 && (
          <p className="admin-vazio" style={{ marginTop: 16 }}>
            Nenhum Radar registrado neste ambiente. Assim que um Radar existir e alguém avaliar os
            achados dele, os números aparecem aqui.
          </p>
        )}

        {!carregando && !erro && avaliacao && radares.length > 0 && (
          <>
            {totalDeVotos === 0 && (
              <p className="admin-vazio" style={{ marginTop: 16 }}>
                Nenhum voto registrado ainda nos últimos {fmtInt(avaliacao.dias)} dias. As tabelas
                abaixo mostram o que o assistente propôs; a avaliação começa no primeiro voto.
              </p>
            )}

            {radares.map((radar) => (
              <section key={radar.radarId} style={{ marginTop: 20 }}>
                <div className="admin-subtitle">
                  {radar.rotulo}
                  {!radar.registrado && (
                    <span className="cilia-badge badge-laranja" style={{ marginLeft: 8 }}>
                      Fora do catálogo
                    </span>
                  )}
                </div>

                <div className="admin-card" style={{ marginTop: 12 }}>
                  <div className="admin-card-title">Avaliação por consulta</div>
                  {radar.porFerramenta.length === 0 ? (
                    <p className="admin-vazio">Nenhum achado deste Radar no período.</p>
                  ) : (
                    <div style={{ overflowX: 'auto' }}>
                      <table className="admin-table">
                        <thead>
                          <tr>
                            <th style={{ textAlign: 'left' }}>Consulta</th>
                            <th style={{ textAlign: 'left' }}>Recortes distintos</th>
                            <th style={{ textAlign: 'left' }}>Úteis</th>
                            <th style={{ textAlign: 'left' }}>Não úteis</th>
                          </tr>
                        </thead>
                        <tbody>
                          {radar.porFerramenta.map((linha) => (
                            <tr key={linha.ferramenta}>
                              <td title={linha.ferramenta}>{linha.ferramenta}</td>
                              <td>{fmtInt(linha.achados)}</td>
                              <td>{fmtInt(linha.up)}</td>
                              <td>{fmtInt(linha.down)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                <div className="admin-card" style={{ marginTop: 16 }}>
                  <div className="admin-card-title">Recortes avaliados</div>
                  <p className="admin-hint">
                    Cada linha é um recorte que o assistente já propôs. Clique para ver os dias em
                    que ele apareceu e o que a equipe escreveu.
                  </p>

                  <div className="admin-tags-row" style={{ marginBottom: 8 }}>
                    {(['TODOS', 'APROVADO', 'REPROVADO', 'NEUTRO'] as const).map((opcao) => (
                      <button
                        key={opcao}
                        type="button"
                        className={
                          filtroDeEstado === opcao ? 'admin-btn admin-btn-primary' : 'admin-btn'
                        }
                        onClick={() => setFiltroDeEstado(opcao)}
                      >
                        {opcao === 'TODOS' ? 'Todos' : ROTULOS_DE_ESTADO[opcao]}
                      </button>
                    ))}
                  </div>

                  {radar.assinaturas.filter(
                    (a) => filtroDeEstado === 'TODOS' || a.estado === filtroDeEstado,
                  ).length === 0 ? (
                    <p className="admin-vazio">Nenhum recorte neste filtro.</p>
                  ) : (
                    <div style={{ overflowX: 'auto' }}>
                      <table className="admin-table">
                        <thead>
                          <tr>
                            <th style={{ textAlign: 'left' }}>Achado</th>
                            <th style={{ textAlign: 'left' }}>Recorte</th>
                            <th style={{ textAlign: 'left' }}>Estado</th>
                            <th style={{ textAlign: 'left' }}>Úteis</th>
                            <th style={{ textAlign: 'left' }}>Não úteis</th>
                            <th style={{ textAlign: 'left' }}>Dias em que apareceu</th>
                            <th style={{ textAlign: 'left' }}>Primeira</th>
                            <th style={{ textAlign: 'left' }}>Última</th>
                          </tr>
                        </thead>
                        <tbody>
                          {radar.assinaturas
                            .filter((a) => filtroDeEstado === 'TODOS' || a.estado === filtroDeEstado)
                            .map((assinatura) => {
                              const recorte = descreverParametros(assinatura.parametros);
                              return (
                                <tr
                                  key={assinatura.assinatura}
                                  className="do-linha-clicavel"
                                  onClick={() => setAssinaturaAberta(assinatura)}
                                >
                                  <td title={assinatura.ultimoTitulo}>{assinatura.ultimoTitulo}</td>
                                  <td title={`${assinatura.ferramenta} · ${recorte}`}>
                                    {assinatura.ferramenta} · {recorte}
                                  </td>
                                  <td>
                                    <span className={CLASSES_DE_ESTADO[assinatura.estado]}>
                                      {ROTULOS_DE_ESTADO[assinatura.estado]}
                                    </span>
                                  </td>
                                  <td>{fmtInt(assinatura.votosUp)}</td>
                                  <td>{fmtInt(assinatura.votosDown)}</td>
                                  <td>{fmtInt(assinatura.aparicoes)}</td>
                                  <td>{fmtDia(assinatura.primeiraAparicao)}</td>
                                  <td>{fmtDia(assinatura.ultimaAparicao)}</td>
                                </tr>
                              );
                            })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                <div className="admin-card" style={{ marginTop: 16 }}>
                  <div className="admin-card-title">Temas</div>
                  <p className="admin-hint">
                    Padrões entre recortes diferentes. Um tema só passa a orientar o assistente
                    quando reúne recortes distintos suficientes — a coluna diz se ele já está
                    agindo.
                  </p>
                  {radar.temas.length === 0 ? (
                    <p className="admin-vazio">Nenhum tema formado ainda.</p>
                  ) : (
                    <div style={{ overflowX: 'auto' }}>
                      <table className="admin-table">
                        <thead>
                          <tr>
                            <th style={{ textAlign: 'left' }}>Tema</th>
                            <th style={{ textAlign: 'left' }}>Nível</th>
                            <th style={{ textAlign: 'left' }}>Estado</th>
                            <th style={{ textAlign: 'left' }}>Já está agindo</th>
                            <th style={{ textAlign: 'left' }}>Recortes</th>
                            <th style={{ textAlign: 'left' }}>Úteis</th>
                            <th style={{ textAlign: 'left' }}>Não úteis</th>
                          </tr>
                        </thead>
                        <tbody>
                          {radar.temas.map((tema) => (
                            <tr key={`${tema.nivel}-${tema.chave}`}>
                              <td title={tema.descricao}>{tema.descricao}</td>
                              <td title={tema.chave}>{tema.nivel}</td>
                              <td>
                                <span className={CLASSES_DE_ESTADO[tema.estado]}>
                                  {ROTULOS_DE_ESTADO[tema.estado]}
                                </span>
                              </td>
                              <td>{tema.duro ? 'Sim' : 'Ainda não'}</td>
                              <td>{fmtInt(tema.assinaturas)}</td>
                              <td>{fmtInt(tema.votosUp)}</td>
                              <td>{fmtInt(tema.votosDown)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                <div className="admin-card" style={{ marginTop: 16 }}>
                  <div className="admin-card-title">
                    Achados por dia (últimos {fmtInt(avaliacao.dias)} dias)
                  </div>
                  <p className="admin-hint">
                    Inclui os <strong>acompanhamentos</strong> (recortes aprovados, recalculados
                    todo dia) e os <strong>suprimidos</strong> — propostas que o assistente tentou
                    repetir e o sistema barrou porque a equipe já as havia reprovado. Os suprimidos
                    nunca aparecem no Radar.
                  </p>
                  {radar.achados.length === 0 ? (
                    <p className="admin-vazio">Nenhum achado deste Radar no período.</p>
                  ) : (
                    <div style={{ overflowX: 'auto' }}>
                      <table className="admin-table">
                        <thead>
                          <tr>
                            <th style={{ textAlign: 'left' }}>Dia</th>
                            <th style={{ textAlign: 'left' }}>Achado</th>
                            <th style={{ textAlign: 'left' }}>Tipo</th>
                            <th style={{ textAlign: 'left' }}>Úteis</th>
                            <th style={{ textAlign: 'left' }}>Não úteis</th>
                            <th style={{ textAlign: 'left' }}>Motivos</th>
                          </tr>
                        </thead>
                        <tbody>
                          {radar.achados.map((achado) => {
                            const motivos = Object.entries(achado.motivos)
                              .map(([m, n]) => `${ROTULOS_DE_MOTIVO[m] ?? m}: ${n}`)
                              .join(' · ');
                            return (
                              <tr key={achado.id}>
                                <td>{fmtDia(achado.dia)}</td>
                                <td title={achado.titulo}>{achado.titulo}</td>
                                <td>
                                  {achado.suprimido
                                    ? 'Suprimido'
                                    : achado.origem === 'ACOMPANHAMENTO'
                                      ? 'Acompanhamento'
                                      : 'Proposta'}
                                </td>
                                <td>{fmtInt(achado.up)}</td>
                                <td>{fmtInt(achado.down)}</td>
                                <td title={motivos}>{motivos || '-'}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </section>
            ))}

            <div className="admin-card" style={{ marginTop: 20 }}>
              <div className="admin-card-title">Histórico de análises</div>
              <p className="admin-hint">
                Cada rodada de análise, com o desfecho e o custo em idas ao modelo. Para o custo em
                dólares, veja <Link to={ADMIN_SISTEMA_CONSUMO_IA()}>Consumo de IA</Link>.
              </p>
              {!exploracoes || exploracoes.exploracoes.length === 0 ? (
                <p className="admin-vazio">
                  Nenhuma análise registrada ainda. O registro começa na primeira análise depois que
                  esta versão entrou no ar.
                </p>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left' }}>Dia</th>
                        <th style={{ textAlign: 'left' }}>Radar</th>
                        <th style={{ textAlign: 'left' }}>Disparo</th>
                        <th style={{ textAlign: 'left' }}>Desfecho</th>
                        <th style={{ textAlign: 'left' }}>Descartadas</th>
                        <th style={{ textAlign: 'left' }}>Idas ao modelo</th>
                        <th style={{ textAlign: 'left' }}>Tokens</th>
                        <th style={{ textAlign: 'left' }}>Concluída em</th>
                      </tr>
                    </thead>
                    <tbody>
                      {exploracoes.exploracoes.map((linha) => (
                        <tr key={linha.id}>
                          <td>{fmtDia(linha.dia)}</td>
                          <td title={linha.chaveDaJanela}>{linha.radarId}</td>
                          <td>{linha.disparadoPor === 'CRON' ? 'Automático' : 'Ao abrir a tela'}</td>
                          <td title={linha.aviso ?? undefined}>
                            <span className={CLASSES_DE_STATUS[linha.status] ?? 'cilia-badge badge-cinza'}>
                              {linha.status === 'CONCLUIDA'
                                ? 'Concluída'
                                : linha.status === 'SEM_PROPOSTA'
                                  ? 'Sem proposta'
                                  : linha.status === 'EM_ANDAMENTO'
                                    ? 'Em andamento'
                                    : 'Falhou'}
                            </span>
                          </td>
                          <td>{fmtInt(linha.candidatosDescartados)}</td>
                          <td>{fmtInt(linha.idasAoModelo)}</td>
                          <td>{fmtInt(linha.tokensEntrada + linha.tokensSaida)}</td>
                          <td>{fmtInstante(linha.concluidoEm)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
          </>
        )}
      </div>

      {assinaturaAberta && (
        <div className="modal-overlay">
          <div className="do-modal do-modal-largo">
            <div className="do-modal-header">
              <span className="do-modal-titulo">{assinaturaAberta.ultimoTitulo}</span>
              <button
                type="button"
                className="close-button"
                onClick={() => setAssinaturaAberta(null)}
                aria-label="Fechar"
              >
                &times;
              </button>
            </div>

            <div className="do-modal-corpo">
              <p className="do-modal-apoio">
                Consulta <strong>{assinaturaAberta.ferramenta}</strong>, recorte{' '}
                {descreverParametros(assinaturaAberta.parametros)}. Estado{' '}
                <span className={CLASSES_DE_ESTADO[assinaturaAberta.estado]}>
                  {ROTULOS_DE_ESTADO[assinaturaAberta.estado]}
                </span>{' '}
                com {fmtInt(assinaturaAberta.votosUp)} úteis e {fmtInt(assinaturaAberta.votosDown)}{' '}
                não úteis.
              </p>

              {achadosDaAssinatura(assinaturaAberta.assinatura).length === 0 ? (
                <p className="do-modal-apoio">
                  Este recorte não apareceu nos últimos {fmtInt(avaliacao?.dias ?? dias)} dias.
                  Aumente o período para ver as aparições anteriores.
                </p>
              ) : (
                <div className="do-tabela-wrapper">
                  <table className="do-tabela">
                    <thead>
                      <tr>
                        <th>Dia</th>
                        <th>Tipo</th>
                        <th>Úteis</th>
                        <th>Não úteis</th>
                        <th>Motivos</th>
                        <th>O que a equipe escreveu</th>
                      </tr>
                    </thead>
                    <tbody>
                      {achadosDaAssinatura(assinaturaAberta.assinatura).map((achado) => {
                        const motivos = Object.entries(achado.motivos)
                          .map(([m, n]) => `${ROTULOS_DE_MOTIVO[m] ?? m}: ${n}`)
                          .join(' · ');
                        const textos = achado.textos.join(' | ');
                        return (
                          <tr key={achado.id}>
                            <td>{fmtDia(achado.dia)}</td>
                            <td>
                              {achado.suprimido
                                ? 'Suprimido'
                                : achado.origem === 'ACOMPANHAMENTO'
                                  ? 'Acompanhamento'
                                  : 'Proposta'}
                            </td>
                            <td>{fmtInt(achado.up)}</td>
                            <td>{fmtInt(achado.down)}</td>
                            <td title={motivos}>{motivos || '-'}</td>
                            <td title={textos}>{textos || '-'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <ComoFunciona
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        titulo="Como Funciona - Radar"
        secoes={secoesDeAjuda(avaliacao)}
      />
    </div>
  );
};

export default RadarAdminPage;
